import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRightLeft, Check, FileText, Pencil, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiBlob, ApiError, openBlob } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsset, useAssetMovements } from '../lib/queries';
import { formatDate, formatDateTime, formatMoney, MOVEMENT_LABELS, SOURCE_LABELS } from '../lib/format';
import { DataPoint, ErrorNote, Spinner, StatusPill, cn } from '../components/ui';
import { MoveSheet } from '../components/MoveSheet';
import { LabelSheet } from '../components/LabelSheet';
import { AssetEditSheet } from '../components/AssetEditSheet';
import { PhotoSection } from '../components/PhotoSection';

/** Alles zu einem Gerät: Stammdaten, aktueller Halter und die volle Historie. */
export function AssetDetailPage() {
  const params = useParams({ strict: false }) as { assetId?: string };
  const assetId = Number(params.assetId);
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [moveOpen, setMoveOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [datevDraft, setDatevDraft] = useState<string | null>(null);

  const { data: asset, isLoading, error } = useAsset(assetId);
  const movements = useAssetMovements(assetId);

  // Dieselbe Abfrage wie im Zustandsbereich — TanStack Query liefert sie aus
  // dem Zwischenspeicher, es wird also nicht doppelt geladen.
  const photos = useQuery({
    queryKey: ['asset-photos', assetId],
    queryFn: () =>
      api<{ id: number; url: string; note: string | null; movement_id: number | null }[]>(
        `/assets/${assetId}/photos`,
      ),
    enabled: assetId > 0,
  });

  /** Fotos nach dem Vorgang gruppieren, zu dem sie gehören. */
  const fotosZuVorgang = new Map<number, { id: number; url: string; note: string | null }[]>();
  for (const foto of photos.data ?? []) {
    if (!foto.movement_id) continue;
    const liste = fotosZuVorgang.get(foto.movement_id) ?? [];
    liste.push(foto);
    fotosZuVorgang.set(foto.movement_id, liste);
  }

  const saveDatev = useMutation({
    mutationFn: (value: string) =>
      api(`/assets/${assetId}`, { method: 'PATCH', body: { datev_id: value || null } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['asset', assetId] });
      setDatevDraft(null);
      toast.success('Datev-Verknüpfung gespeichert');
    },
    onError: (caught) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen'),
  });

  /** Protokoll im neuen Tab öffnen — von dort direkt druckbar. */
  async function openProtocol(movementId: number): Promise<void> {
    try {
      const { blob } = await apiBlob(`/movements/${movementId}/protocol`);
      openBlob(blob);
    } catch {
      toast.error('Das Protokoll konnte nicht erzeugt werden');
    }
  }

  if (isLoading) return <Spinner label="Gerät wird geladen …" />;
  if (error) return <ErrorNote error={error} />;
  if (!asset) return null;

  return (
    <div>
      <Link
        to="/geraete"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--color-fg3)] no-underline transition-colors hover:text-[var(--color-accent)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Alle Geräte
      </Link>

      {/* ---------- Kopf ---------- */}
      <header className="animate-fade-up mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="eyebrow mb-1.5">Inventarnummer {asset.asset_tag}</p>
            <h1 className="font-[family-name:var(--font-display)] text-4xl leading-tight sm:text-5xl">
              {asset.model?.name ?? 'Gerät ohne Modell'}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <StatusPill name={asset.status.name} archived={asset.status.is_archived} />
              {asset.holder ? (
                <span className="text-[var(--color-fg2)]">
                  {asset.holder.type === 'employee' ? 'bei ' : 'am Standort '}
                  {asset.holder.type === 'employee' ? (
                    <Link
                      to="/personen/$employeeId"
                      params={{ employeeId: String(asset.holder.id) }}
                      className="font-semibold"
                    >
                      {asset.holder.name}
                    </Link>
                  ) : (
                    <strong>{asset.holder.name}</strong>
                  )}
                </span>
              ) : (
                <span className="text-sm text-[var(--color-fg3)]">nicht zugewiesen</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {auth.can('label.print') && (
              <button type="button" onClick={() => setLabelOpen(true)} className="btn btn-ghost btn-md">
                <Tag className="h-4 w-4" aria-hidden />
                Etikett
              </button>
            )}
            {auth.can('asset.write') && (
              <button type="button" onClick={() => setEditOpen(true)} className="btn btn-ghost btn-md">
                <Pencil className="h-4 w-4" aria-hidden />
                Bearbeiten
              </button>
            )}
            {auth.can('asset.move') && (
              <button type="button" onClick={() => setMoveOpen(true)} className="btn btn-accent btn-md">
                <ArrowRightLeft className="h-4 w-4" aria-hidden />
                Übergeben
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
        {/* ---------- Stammdaten ---------- */}
        <section className="surface animate-fade-up pad">
          <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">Stammdaten</h2>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
            <DataPoint label="Seriennummer">
              <span className="tabular">{asset.serial}</span>
            </DataPoint>
            <DataPoint label="Kategorie">{asset.category?.name}</DataPoint>
            <DataPoint label="Hersteller">{asset.manufacturer?.name}</DataPoint>
            <DataPoint label="Standort">{asset.home_location?.name}</DataPoint>
            <DataPoint label="Kaufdatum">{formatDate(asset.purchase_date)}</DataPoint>
            {auth.can('cost.read') && (
              <DataPoint label="Kaufpreis">{formatMoney(asset.purchase_cost)}</DataPoint>
            )}
            <DataPoint label="Lieferant">{asset.supplier?.name}</DataPoint>
            <DataPoint label="Bestellnummer">{asset.order_number}</DataPoint>
            <DataPoint label="Etikett gedruckt">
              {asset.label_printed_at ? formatDate(asset.label_printed_at) : 'noch nicht'}
            </DataPoint>
            <DataPoint label="Aus Snipe-IT">
              {asset.snipe_id ? `ID ${asset.snipe_id}` : 'neu erfasst'}
            </DataPoint>
          </dl>

          {/* Datev-Verknüpfung: direkt hier pflegbar, ohne Umweg über Bearbeiten. */}
          <div className="mt-5 border-t border-[var(--color-line)] pt-4">
            <span className="eyebrow mb-1.5 block">Datev-Anlagengut</span>
            {datevDraft === null ? (
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'tabular text-lg',
                    !asset.datev_id && 'text-[var(--color-fg3)] italic',
                  )}
                >
                  {asset.datev_id || 'nicht verknüpft'}
                </span>
                {auth.can('asset.write') && (
                  <button
                    type="button"
                    onClick={() => setDatevDraft(asset.datev_id ?? '')}
                    className="btn btn-quiet btn-sm"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    {asset.datev_id ? 'ändern' : 'verknüpfen'}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={datevDraft}
                  onChange={(event) => setDatevDraft(event.target.value)}
                  placeholder="Anlagennummer aus Datev"
                  className="field"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') saveDatev.mutate(datevDraft);
                    if (event.key === 'Escape') setDatevDraft(null);
                  }}
                />
                <button
                  type="button"
                  onClick={() => saveDatev.mutate(datevDraft)}
                  className="btn btn-primary btn-md"
                  aria-label="Speichern"
                >
                  <Check className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setDatevDraft(null)}
                  className="btn btn-ghost btn-md"
                  aria-label="Abbrechen"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
          </div>

          {asset.notes && (
            <div className="mt-5 border-t border-[var(--color-line)] pt-4">
              <span className="eyebrow mb-1.5 block">Notizen</span>
              <p className="text-sm whitespace-pre-wrap text-[var(--color-fg1)]">{asset.notes}</p>
            </div>
          )}
        </section>

        {/* ---------- Historie ---------- */}
        <section className="surface animate-fade-up pad" style={{ animationDelay: '60ms' }}>
          <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">Verlauf</h2>

          {movements.isLoading && <Spinner />}

          {movements.data && (
            <ol className="relative space-y-4 border-l border-[var(--color-line)] pl-5">
              {movements.data.rows.map((movement) => (
                <li key={movement.id} className="relative">
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-1.5 -left-[1.4375rem] h-2 w-2 rounded-full ring-4 ring-[var(--color-bg-elevated)]',
                      movement.kind === 'checkout' || movement.kind === 'transfer'
                        ? 'bg-[var(--color-khadi-forest-600)]'
                        : movement.kind === 'checkin'
                          ? 'bg-[var(--color-khadi-saffron-400)]'
                          : movement.kind === 'create'
                            ? 'bg-[var(--color-accent)]'
                            : 'bg-[var(--color-khadi-forest-200)]',
                    )}
                  />
                  <p className="text-sm text-[var(--color-fg)]">
                    <strong>{MOVEMENT_LABELS[movement.kind] ?? movement.kind}</strong>
                    {movement.to?.name && <> an {movement.to.name}</>}
                    {movement.from?.name && movement.kind !== 'checkout' && (
                      <span className="text-[var(--color-fg3)]"> (von {movement.from.name})</span>
                    )}
                  </p>
                  {movement.note && (
                    <p className="mt-0.5 text-sm text-[var(--color-fg2)] italic">{movement.note}</p>
                  )}
                  <p className="mt-0.5 text-sm text-[var(--color-fg3)]">
                    {formatDateTime(movement.occurred_at)}
                    {movement.actor ? ` · ${movement.actor}` : ''}
                    {movement.source !== 'ui' ? ` · ${SOURCE_LABELS[movement.source] ?? movement.source}` : ''}
                  </p>
                  {/* Zum Vorgang gehörende Zustandsfotos — beim Ausmustern der
                      eigentliche Nachweis, deshalb direkt in der Zeile. */}
                  {fotosZuVorgang.get(movement.id)?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {fotosZuVorgang.get(movement.id)!.map((foto) => (
                        <a
                          key={foto.id}
                          href={foto.url}
                          target="_blank"
                          rel="noopener"
                          className="block overflow-hidden rounded-[var(--radius-sm)] border border-[var(--color-line)]"
                          title={foto.note ?? 'Zustandsfoto'}
                        >
                          <img
                            src={foto.url}
                            alt={foto.note ?? 'Zustandsfoto'}
                            loading="lazy"
                            className="h-20 w-20 object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {movement.confirmation_pending && (
                      <span className="pill pill-stock">Bestätigung ausstehend</span>
                    )}
                    {movement.confirmed_at && (
                      <span className="pill pill-deployed">
                        Empfang bestätigt {formatDate(movement.confirmed_at)}
                      </span>
                    )}
                    {['checkout', 'checkin', 'transfer'].includes(movement.kind) && (
                      <button
                        type="button"
                        onClick={() => void openProtocol(movement.id)}
                        className="btn btn-quiet btn-sm px-2 text-sm"
                      >
                        <FileText className="h-3.5 w-3.5" aria-hidden />
                        Protokoll
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* Zustandsdokumentation unterhalb der Stammdaten — sie gehört zum Gerät,
          nicht in einen Dialog, damit sie beim Nachsehen sofort da ist. */}
      <div className="mt-4">
        <PhotoSection assetId={asset.id} />
      </div>

      {moveOpen && <MoveSheet asset={asset} onClose={() => setMoveOpen(false)} />}
      {labelOpen && <LabelSheet assetIds={[asset.id]} onClose={() => setLabelOpen(false)} />}
      {editOpen && <AssetEditSheet asset={asset} onClose={() => setEditOpen(false)} />}
    </div>
  );
}
